import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import queryClient, { SyncedSiteToggleInputDto } from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import {
  SyncedSiteDto,
  SyncedSiteSuggestionDto,
  SyncedSiteSyncReason,
  SyncedSiteSyncResultDto,
} from '@/Domain/api/KnowledgeBase/dto/SyncedSite.dto';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { extractUrlHost } from '@/Domain/helper/extractUrlHost';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  SITEMAP_CONSUMED_KEY,
  setSiteMapPending,
} from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

// While any site reports a run in flight, poll the list so the row flips from
// "checking" to its result without a manual refresh (there is no websocket).
const SYNC_POLL_INTERVAL_MS = 3000;

const SYNC_REASON_TOAST: Record<SyncedSiteSyncReason, string> = {
  sync_disabled: 'synced_sites.sync_toast.sync_disabled',
  account_not_paid: 'synced_sites.sync_toast.account_not_paid',
  already_running: 'synced_sites.sync_toast.already_running',
};

/**
 * Synced websites list + per-site sync toggle. Toasts and list invalidation
 * live in the shared mutation defaults; `togglingIds` tracks in-flight toggles
 * so each switch can block re-clicks independently. `runSync` triggers an
 * on-demand check and the list self-polls until every run settles.
 * `keepInSync` maps a suggested website so the review opens with sync preselected.
 */
export const useSyncedSites = () => {
  const { t } = useTranslation('knowledge_base');
  const { capture } = usePostHogAnalytics();
  const query = useQuery<SyncedSiteDto[]>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.list],
    refetchInterval: (q) =>
      Array.isArray(q.state.data) && q.state.data.some((site) => site.sync_running)
        ? SYNC_POLL_INTERVAL_MS
        : false,
  });

  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set());
  const mutation = useMutation<SyncedSiteDto, unknown, SyncedSiteToggleInputDto>({
    mutationKey: [QueryKey.knowledgeBase.syncedSites.update],
  });

  const clearToggling = useCallback(
    (siteId: number) =>
      setTogglingIds((prev) => {
        const next = new Set(prev);
        next.delete(siteId);
        return next;
      }),
    [],
  );

  const toggle = useCallback(
    (siteId: number, enabled: boolean) => {
      setTogglingIds((prev) => new Set(prev).add(siteId));
      mutation.mutate(
        { siteId, sync_enabled: enabled },
        { onSettled: () => clearToggling(siteId) },
      );
    },
    [mutation, clearToggling],
  );

  const [syncingIds, setSyncingIds] = useState<Set<number>>(new Set());
  const syncMutation = useMutation<SyncedSiteSyncResultDto, unknown, { siteId: number }>({
    mutationKey: [QueryKey.knowledgeBase.syncedSites.sync],
  });

  const runSync = useCallback(
    (siteId: number) => {
      setSyncingIds((prev) => new Set(prev).add(siteId));
      syncMutation.mutate(
        { siteId },
        {
          onSuccess: (result) => {
            if (result.dispatched) {
              toast.success(t('synced_sites.sync_toast.started'));
            } else if (result.reason) {
              // dispatched:false is a normal race outcome, never an error toast.
              toast.info(t(SYNC_REASON_TOAST[result.reason]));
            }
            void queryClient.invalidateQueries({
              queryKey: [QueryKey.knowledgeBase.syncedSites.list],
            });
          },
          onError: () => toast.error(t('synced_sites.sync_toast.failed')),
          onSettled: () =>
            setSyncingIds((prev) => {
              const next = new Set(prev);
              next.delete(siteId);
              return next;
            }),
        },
      );
    },
    [syncMutation, t],
  );

  const suggestionsQuery = useQuery<SyncedSiteSuggestionDto[]>({
    queryKey: [QueryKey.knowledgeBase.syncedSites.suggestions],
  });

  const [startingRootUrl, setStartingRootUrl] = useState<string | null>(null);
  const startMapMutation = useMutation<unknown, unknown, { url: string; keep_in_sync: boolean }>({
    mutationKey: [QueryKey.knowledgeBase.siteMapPreview.start],
  });

  const keepInSync = useCallback(
    (rootUrl: string) => {
      setStartingRootUrl(rootUrl);
      startMapMutation.mutate(
        { url: rootUrl, keep_in_sync: true },
        {
          onSuccess: () => {
            localStorage.removeItem(SITEMAP_CONSUMED_KEY);
            setSiteMapPending();
            capture(PostHogEventEnum.SiteScrapeMapStarted, {
              url_host: extractUrlHost(rootUrl),
              source: 'synced_sites_suggestion',
            });
            toast.success(t('synced_sites.suggestions.started'));
          },
          onError: () => toast.error(t('synced_sites.suggestions.failed')),
          onSettled: () => setStartingRootUrl(null),
        },
      );
    },
    [startMapMutation, capture, t],
  );

  const sites = Array.isArray(query.data) ? query.data : [];
  const suggestions = Array.isArray(suggestionsQuery.data) ? suggestionsQuery.data : [];

  return {
    sites,
    suggestions,
    isLoading: query.isLoading,
    toggle,
    togglingIds,
    runSync,
    syncingIds,
    keepInSync,
    startingRootUrl,
  };
};
